import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { AuthService } from '../services/auth';
import PinLoginForm from './PinLoginForm';

interface LoginWithPinProps {
  onLoginSuccess: (user: any, token: string) => void;
  title?: string;
  subtitle?: string;
  onLogoClick?: () => void;
}

export const LoginWithPin: React.FC<LoginWithPinProps> = ({
  onLoginSuccess,
  title = "Sign In",
  subtitle = "Enter your email and 4-digit PIN to continue",
  onLogoClick
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePinLogin = async (email: string, pin: string) => {
    setLoading(true);
    setError('');

    try {
      const result = await AuthService.loginWithPin(email, pin);
      onLoginSuccess(result.user, result.token);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClearError = () => {
    setError('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 500 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LoginIcon 
              sx={{ 
                fontSize: 48, 
                color: 'primary.main', 
                mb: 2,
                cursor: onLogoClick ? 'pointer' : 'default',
                transition: 'transform 0.2s',
                '&:hover': onLogoClick ? {
                  transform: 'scale(1.1)'
                } : {}
              }}
              onClick={onLogoClick}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <PinLoginForm
            onSubmit={handlePinLogin}
            loading={loading}
            error={error}
            onClearError={handleClearError}
          />

          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have your PIN? Contact the party organizer for assistance.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};